import * as React from "react";
import {cva, type VariantProps} from "class-variance-authority";
import {cn} from "@repo/ui/lib/utils";
import styles from "./typography.module.css";

/**
 * Single source of truth for Typography `variant` keys (also used by CMS schemas).
 *
 * Discovery CMS uses the PACT keys in `cmsTypographyVariantKeys`. Academy2
 * variant names remain on the component for Careers / other apps that import
 * `aops.css` only.
 */
const typographyVariantClasses = {
	/* PACT / website */
	h1: styles.h1,
	h2: styles.h2,
	h3: styles.h3,
	h4: styles.h4,
	subtitle: styles.subtitle,
	body: styles.body,
	bodySmall: styles.bodySmall,
	bodyLarge: styles.bodyLarge,
	buttonXl: styles.buttonXl,
	buttonLg: styles.buttonLg,
	buttonMd: styles.buttonMd,
	buttonSm: styles.buttonSm,
	tab: styles.tab,
	eyebrow: styles.eyebrow,
	eyebrowLg: styles.eyebrowLg,
	tag: styles.tag,
	caption: styles.caption,
	stat: styles.stat,
	footerNav: styles.footerNav,

	/* Academy2 (Careers / non-website consumers) */
	displayLg: styles.displayLg,
	displayMd: styles.displayMd,
	displaySm: styles.displaySm,
	displayXs: styles.displayXs,
	bodyMd: styles.bodyMd,
	bodySm: styles.bodySm,
	labelXs: styles.labelXs,
	displayXl: styles.displayXl,
	bodyXl: styles.bodyXl,
	bodyLg: styles.bodyLg,

	/* Legacy shadcn-oriented variants */
	h5: styles.h5,
	h6: styles.h6,
	p: styles.p,
	large: styles.large,
	small: styles.small,
	muted: styles.muted,
	lead: styles.lead,
} as const;

export type TypographyVariantName = keyof typeof typographyVariantClasses;

export const typographyVariantKeys = Object.keys(
	typographyVariantClasses
) as TypographyVariantName[];

/**
 * Variant keys exposed in the Sanity CMS dropdowns for Discovery (PACT ramp).
 */
export const cmsTypographyVariantKeys = [
	"h1",
	"h2",
	"h3",
	"h4",
	"subtitle",
	"body",
	"bodySmall",
	"bodyLarge",
	"eyebrow",
	"eyebrowLg",
	"tag",
	"caption",
	"stat",
	"footerNav",
	"tab",
] as const satisfies readonly TypographyVariantName[];

const typographyVariants = cva("", {
	variants: {
		variant: typographyVariantClasses,
	},
	defaultVariants: {
		variant: "p",
	},
});

/**
 * Default HTML element rendered for each Typography variant when no `as` prop
 * is supplied (similar to Material UI). Declared `as const` so consumers (e.g.
 * Sanity schema helpers) can derive the exact set of tag names from the values
 * without re-listing them.
 */
export const defaultVariantMapping = {
	h1: "h1",
	h2: "h2",
	h3: "h3",
	h4: "h4",
	subtitle: "p",
	body: "p",
	bodySmall: "p",
	bodyLarge: "p",
	buttonXl: "span",
	buttonLg: "span",
	buttonMd: "span",
	buttonSm: "span",
	tab: "span",
	eyebrow: "span",
	eyebrowLg: "span",
	tag: "span",
	caption: "span",
	stat: "p",
	footerNav: "span",
	displayXl: "h1",
	displayLg: "h2",
	displayMd: "h3",
	displaySm: "h4",
	displayXs: "h5",
	bodyXl: "p",
	bodyLg: "p",
	bodyMd: "p",
	bodySm: "p",
	labelXs: "span",
	h5: "h5",
	h6: "h6",
	p: "p",
	large: "p",
	small: "p",
	muted: "p",
	lead: "p",
} as const satisfies Record<TypographyVariantName, React.ElementType>;

export type SemanticElement =
	(typeof defaultVariantMapping)[keyof typeof defaultVariantMapping];

// Deduplicated HTML tag names used by `defaultVariantMapping`.
export const semanticElementValues: readonly SemanticElement[] = Array.from(
	new Set(Object.values(defaultVariantMapping))
) as readonly SemanticElement[];

export interface TypographyProps
	extends
		React.HTMLAttributes<HTMLElement>,
		VariantProps<typeof typographyVariants> {
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * If not provided, defaults to the appropriate element for the variant.
	 */
	as?: React.ElementType;
}

/**
 * Display a consistently styled text element, with a variety of built-in variants.
 * For example, `<Typography variant="h1">Hello</Typography>` will render an `<h1>` element with the appropriate PACT H1 styles when `website.css` is loaded.
 * You can override the component used by passing the `as` prop, for example:
 *  `<Typography as="h2" variant="h1">Hello</Typography>` will render an `<h2>` element with the appearance of an H1.
 * You can also pass a className to the component to override any individual styles.
 * By default, the component will render a `<p>` element.
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
	({className, variant, as, ...props}, ref) => {
		const Component = as || defaultVariantMapping[variant || "p"] || "p";

		return (
			<Component
				ref={ref}
				className={cn(typographyVariants({variant}), className)}
				data-variant={variant}
				{...props}
			/>
		);
	}
);

Typography.displayName = "Typography";

export {Typography, typographyVariants};
